// Shared marketing-content sections for a program. Used by:
//   • /programs/:slug (public marketing) — full page including hero+footer
//   • /app/programs/:slug (member shell, info-only mode for inactive
//     programs) — embedded inside the member page below the program hero
//
// Each section is a thin renderer over the content-as-data shape defined
// in the local marketing JSON files (`/src/content/marketing/programs/*.json`).
// All sections gracefully no-op when the corresponding block is missing.
import { Eyebrow, Headline } from "@/components/Editorial";
import TopWedge from "@/components/TopWedge";
import { C } from "@/lib/brand";

function EditorialBlock({ block, background = C.cream, color = C.ink, accent = C.peachDeep }) {
  return (
    <section style={{ background, color, padding: "96px 24px" }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        {block.eyebrow && <Eyebrow color={accent}>{block.eyebrow}</Eyebrow>}
        {(block.headlineLine1 || block.headline) && (
          <Headline
            before={block.headlineLine1 || block.headline}
            italic={block.headlineLine2 || ""}
            size={64}
            color={color}
            italicColor={accent}
            className="mt-6"
          />
        )}
        {block.body && (
          <div style={{ marginTop: 28, fontSize: 17, lineHeight: 1.7, color, whiteSpace: "pre-line", maxWidth: 720 }}>
            {block.body}
          </div>
        )}
      </div>
    </section>
  );
}

export const PromiseSection = ({ block }) => (
  <EditorialBlock block={block} background={C.bone} accent={C.peachDeep} />
);

export function RealitySection({ block }) {
  const items = block.items || block.pillars || [];
  return (
    <section style={{ background: C.cream, padding: "96px 24px", position: "relative" }}>
      <TopWedge color={C.bone} dir="left" />
      <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 2 }}>
        {block.eyebrow && <Eyebrow color={C.peachDeep}>{block.eyebrow}</Eyebrow>}
        {(block.headlineLine1 || block.headline) && (
          <Headline
            before={block.headlineLine1 || block.headline}
            italic={block.headlineLine2 || ""}
            size={56}
            className="mt-4"
          />
        )}
        <div style={{ marginTop: 56, display: "grid", gridTemplateColumns: "1fr", gap: 28 }} className="md:!grid-cols-3">
          {items.map((p, i) => (
            <div key={i} style={{ borderTop: `2px solid ${C.peachDeep}`, paddingTop: 22 }}>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.peachDeep }}>
                {String(i + 1).padStart(2, "0")} · {(p.title || "").toUpperCase()}
              </div>
              <p style={{ marginTop: 14, fontSize: 15, lineHeight: 1.65, color: C.warmGray }}>{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function HowItWorks({ block }) {
  const pillars = block.pillars || block.items || [];
  return (
    <section style={{ background: C.ink, color: C.cream, padding: "96px 24px" }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        {block.eyebrow && <Eyebrow color={C.peach}>{block.eyebrow}</Eyebrow>}
        {(block.headlineLine1 || block.headline) && (
          <Headline
            before={block.headlineLine1 || block.headline}
            italic={block.headlineLine2 || ""}
            color={C.cream}
            italicColor={C.peach}
            size={56}
            className="mt-4"
          />
        )}
        {block.body && (
          <p style={{ marginTop: 24, fontSize: 16, lineHeight: 1.65, color: "rgba(250,246,240,0.75)", maxWidth: 700 }}>
            {block.body}
          </p>
        )}
        <div style={{ marginTop: 56, display: "grid", gridTemplateColumns: "1fr", gap: 36 }}>
          {pillars.map((p, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 24, alignItems: "start" }}>
              <span
                aria-hidden
                style={{
                  fontFamily: '"Instrument Serif", Georgia, serif',
                  fontStyle: "italic",
                  fontSize: 64,
                  lineHeight: 1,
                  color: C.peach,
                  opacity: 0.75,
                }}
              >
                {String(i + 1).padStart(2, "0")}
              </span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: "0.18em", color: C.peach }}>
                  {(p.title || "").toUpperCase()}
                </div>
                <p style={{ marginTop: 12, fontSize: 16, lineHeight: 1.65, color: "rgba(250,246,240,0.86)" }}>
                  {p.body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SignalList({ title, items, accent, bg, dim }) {
  if (!items || !items.length) return null;
  return (
    <div style={{ background: bg, color: accent, padding: 28, opacity: dim ? 0.92 : 1 }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em" }}>★ {title}</div>
      <ul style={{ margin: "18px 0 0", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 12 }}>
        {items.map((t, i) => (
          <li key={i} style={{ fontSize: 14, lineHeight: 1.55, color: accent, paddingLeft: 16, position: "relative" }}>
            <span aria-hidden style={{ position: "absolute", left: 0, top: 0, fontWeight: 800 }}>·</span>
            {t}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function WhoItsFor({ block }) {
  const fits = block.fits || block.rightFor || [];
  const notFits = block.notFits || block.notRightFor || [];
  return (
    <section style={{ background: C.cream, padding: "96px 24px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        {block.eyebrow && <Eyebrow color={C.peachDeep}>{block.eyebrow}</Eyebrow>}
        {(block.headlineLine1 || block.headline) && (
          <Headline before={block.headlineLine1 || block.headline} italic={block.headlineLine2 || ""} size={48} className="mt-4" />
        )}
        <div style={{ marginTop: 48, display: "grid", gridTemplateColumns: "1fr", gap: 32 }} className="md:!grid-cols-2">
          <SignalList title="RIGHT FOR YOU IF…" items={fits} accent={C.sageDark} bg={C.sage} />
          <SignalList title="MIGHT NOT BE RIGHT IF…" items={notFits} accent={C.peachDark} bg={C.peach} dim />
        </div>
      </div>
    </section>
  );
}

export function Voya9Difference({ block }) {
  const items = block.items || block.pillars || [];
  return (
    <section style={{ background: C.bone, padding: "96px 24px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        {block.eyebrow && <Eyebrow color={C.peachDeep}>{block.eyebrow}</Eyebrow>}
        {(block.headlineLine1 || block.headline) && (
          <Headline before={block.headlineLine1 || block.headline} italic={block.headlineLine2 || ""} size={56} className="mt-4" />
        )}
        <div style={{ marginTop: 48, display: "grid", gridTemplateColumns: "1fr", gap: 28 }} className="md:!grid-cols-3">
          {items.map((p, i) => (
            <div key={i} style={{ background: C.pure, padding: 28, borderTop: `3px solid ${C.peachDeep}` }}>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.peachDeep }}>
                {String(i + 1).padStart(2, "0")} · {(p.title || "").toUpperCase()}
              </div>
              <p style={{ marginTop: 14, fontSize: 14, lineHeight: 1.65, color: C.warmGray }}>{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQItem({ item }) {
  return (
    <details className="faq-item" style={{ borderTop: `1px solid ${C.warmGray3}`, padding: "20px 0" }}>
      <summary style={{ cursor: "pointer", listStyle: "none", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, fontSize: 16, fontWeight: 700, color: C.ink }}>
        <span style={{ flex: 1 }}>{item.q || item.question}</span>
        <span aria-hidden className="faq-toggle" style={{ fontSize: 20, color: C.peachDeep, transition: "transform 0.2s" }}>+</span>
      </summary>
      <div style={{ marginTop: 12, fontSize: 15, lineHeight: 1.65, color: C.warmGray }}>
        {item.a || item.answer}
      </div>
    </details>
  );
}

export function FAQSection({ items }) {
  return (
    <section style={{ background: C.bone, padding: "96px 24px" }}>
      <div style={{ maxWidth: 800, margin: "0 auto" }}>
        <Eyebrow color={C.peachDeep}>★ COMMON QUESTIONS</Eyebrow>
        <Headline before="QUESTIONS," italic="answered." size={56} className="mt-4" />
        <div style={{ marginTop: 40, display: "grid", gap: 0 }}>
          {items.map((q, i) => <FAQItem key={i} item={q} />)}
        </div>
      </div>
    </section>
  );
}

// Convenience wrapper — renders every available section in canonical order
// from a marketing-shaped program object. Caller decides what hero/footer
// chrome to wrap around it.
export default function MarketingProgramSections({ program }) {
  if (!program) return null;
  return (
    <>
      {program.promise && <PromiseSection block={program.promise} />}
      {program.reality && <RealitySection block={program.reality} />}
      {program.howItWorks && <HowItWorks block={program.howItWorks} />}
      {program.whoItsFor && <WhoItsFor block={program.whoItsFor} />}
      {program.voya9Difference && <Voya9Difference block={program.voya9Difference} />}
      {Array.isArray(program.faq) && program.faq.length > 0 && <FAQSection items={program.faq} />}
    </>
  );
}
